import type {
  AccessListItem,
  GroupItemList,
  GroupItemTypes,
} from '@app/application/user-management/types'
import type { AccessListBinding } from '@app/application/user-management/composables/use-access-list'
import { reactive } from 'vue'

import {
  pickerCompanies,
  pickerUsers,
} from '@app/application/user-management/api/groups'
import { accessListFields } from '@app/application/user-management/composables/use-access-list'
import { useGroupPanelStore } from '@app/application/user-management/stores/group-panel'

const PICKERS: {
  [L in GroupItemList]: (
    groupId: number | null,
    query: string,
    signal: AbortSignal
  ) => Promise<GroupItemTypes[L][] | null>
} = {
  members: pickerUsers,
  files: pickerCompanies,
}

export interface GroupItemListBinding<L extends GroupItemList>
  extends AccessListBinding<GroupItemTypes[L]> {
  searchToAdd: (
    query: string,
    signal: AbortSignal
  ) => Promise<GroupItemTypes[L][] | null>
}

export default function useGroupItemList<L extends GroupItemList>(
  list: L,
  toItems: (rows: GroupItemTypes[L][]) => AccessListItem[]
): GroupItemListBinding<L> {
  const store = useGroupPanelStore()

  return reactive({
    ...accessListFields<GroupItemTypes[L]>(
      {
        view: () => store.itemViews[list],
        search: (query) => store.searchItems(list, query),
        loadMore: () => store.loadMoreItems(list),
      },
      toItems
    ),
    searchToAdd: (query: string, signal: AbortSignal) =>
      PICKERS[list](store.openGroupId, query, signal),
  })
}
